import React, { useEffect, useRef, useState } from 'react';

import { getTranslation, keys } from '../../../../../Resources/Translations';
import { ICartSummary } from '../../../FarmasietCart/ICartSummary.csharp';
import { CartSummaryFreeDeliveryInfoV2 } from '../../Components/CartSummaryFreeDeliveryInfo/CartSummaryFreeDeliveryInfoV2';
import { CartItemViewModel } from '../../Item/ViewModels/CartItemViewModel.csharp';

interface SimpleAddToCartProps {
    isVisible: boolean;
    closeWidget: () => void;
    cartPageUrl: string;
    cart: ICartSummary;
    lastUpdatedItem?: string;
    updateTrigger: number;
}

export const SimpleAddToCart = ({ isVisible, closeWidget, cartPageUrl, cart, lastUpdatedItem, updateTrigger }: SimpleAddToCartProps) => {
    const closeBtnRef = useRef<HTMLButtonElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const hideWidgetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hideWidgetAnimationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [activeItem, setActiveItem] = useState<CartItemViewModel | undefined>(undefined);
    const [updateTriggerState, setUpdateTriggerState] = useState<number>(updateTrigger);

    useEffect(() => {
        if (isVisible && lastUpdatedItem) {
            const addedCartItem = cart.items.find((x) => x.code === lastUpdatedItem);
            if (activeItem && addedCartItem && activeItem.code !== addedCartItem.code) {
                replaceItems(addedCartItem);
            } else if (activeItem && addedCartItem && activeItem.code === addedCartItem.code && updateTriggerState !== updateTrigger) {
                setHideWidgetTimeout();
            } else {
                setActiveItem(addedCartItem);
                setHideWidgetTimeout();
            }
        } else if (!isVisible && activeItem) {
            hideWidget();
        }
        setUpdateTriggerState(updateTrigger);
    }, [lastUpdatedItem, isVisible, updateTrigger]);

    useEffect(() => {
        if (activeItem) {
            closeBtnRef.current?.focus({ preventScroll: true });
        }
    }, [activeItem]);

    useEffect(() => {
        return () => {
            clearTimeouts();
        };
    }, []);

    const hideWidget = () => {
        containerRef.current?.classList.add('SimpleAddToCart--Hidden');
        hideWidgetWithTimeout();
    };

    const replaceItems = (addedCartItem: CartItemViewModel) => {
        clearTimeouts();
        containerRef.current?.classList.add('SimpleAddToCart--Hidden');
        setTimeout(() => {
            setActiveItem(addedCartItem);
            containerRef.current?.classList.remove('SimpleAddToCart--Hidden');
            setHideWidgetTimeout();
        }, 200);
    };

    const setHideWidgetTimeout = (longerTimeout = false) => {
        clearTimeouts();
        hideWidgetTimeoutRef.current = setTimeout(
            () => {
                containerRef.current?.classList.add('SimpleAddToCart--Hidden');
                hideWidgetWithTimeout();
            },
            longerTimeout ? 10000 : 5000
        );
    };

    const hideWidgetWithTimeout = () => {
        hideWidgetAnimationTimeoutRef.current = setTimeout(() => {
            closeWidget();
            setActiveItem(undefined);
        }, 200);
    };

    const clearTimeouts = () => {
        hideWidgetTimeoutRef.current && clearTimeout(hideWidgetTimeoutRef.current);
        hideWidgetTimeoutRef.current = null;
        hideWidgetAnimationTimeoutRef.current && clearTimeout(hideWidgetAnimationTimeoutRef.current);
        hideWidgetAnimationTimeoutRef.current = null;
    };

    if (!activeItem) return <></>;

    const { mainName, additionalName } = activeItem.nameInfo;

    return (
        <div className="SimpleAddToCart" ref={containerRef}>
            <div className="SimpleAddToCart__Top">
                <div className="SimpleAddToCart__CheckmarkWrapper">
                    <span className="SimpleAddToCart__CheckmarkIcon" />
                </div>
                <button
                    className="SimpleAddToCart__CloseButton"
                    type="button"
                    onClick={closeWidget}
                    aria-label={getTranslation(keys['cartWidget.closeInfo'])}
                    ref={closeBtnRef}
                />
                <div className="SimpleAddToCart__Main" onFocus={() => setHideWidgetTimeout()} onClick={() => setHideWidgetTimeout()}>
                    <h2 className="SimpleAddToCart__Heading">{getTranslation(keys['cartSummary.addedToCart'])}</h2>
                    <span className="SimpleAddToCart__ItemInfo">{`${mainName}${additionalName && `, ${additionalName}`}`}</span>
                    <a href={cartPageUrl} className="SimpleAddToCart__GoToCartLink">
                        <span>{getTranslation(keys['cartSummary.goToCart'])}</span>
                    </a>
                </div>
            </div>
            {cart.shippingInfo?.cheapestMethodInfo && (
                <div className="SimpleAddToCart__FreeDeliveryWrapper" onFocus={() => setHideWidgetTimeout(true)} onClick={() => setHideWidgetTimeout(true)}>
                    <CartSummaryFreeDeliveryInfoV2
                        shippingInfo={cart.shippingInfo}
                        subTotalWithDiscountsIncludingHelfo={cart.subTotalWithDiscountsIncludingHelfo}
                        deliveryPriceCalculationInfo={cart.deliveryPriceCalculationLogic}
                    />
                </div>
            )}
        </div>
    );
};
